import {
  useGetAllOfficesQuery,
  useGetCompanyDetailsQuery,
  useUpdateCompanyMutation,
} from '@/app/features/control-panel/organization/organization.api';
import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import WithLoader from '@/components/WithLoader';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import useAuth from '@/hooks/useAuth';
import { useTimeZone } from '@/hooks/useTimeZone';
import { CirclePlusIcon, EyeIcon, PencilEdit02Icon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { errorToast, normalizeColumnValue, uploadFileToBucket } from '@/utils';
import { Button, Flex, Space, Switch, type TableProps } from 'antd';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import ManageCompany from './_components/Offices/ManageCompany';
import ManageOffices from './_components/Offices/ManageOffices';

const OFFICES_STATUS_CONFIG = [
  { label: 'Active', value: 'ACTIVE', className: 'status status-success' },
  { label: 'Inactive', value: 'INACTIVE', className: 'status status-error' },
];

const GEO_STATUS_CONFIG = [
  { label: 'Active', value: 'active', className: 'status status-success' },
  { label: 'Inactive', value: 'inactive', className: 'status status-error' },
];

const Offices = () => {
  const { profileInfo, access_token } = useAuth();
  const { getLabelByValue } = useTimeZone();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Use filters hook
  const { page, skip, limit, filters, keyword, handlePagination } =
    useAppFilters(FILTER_KEYS.ORG_OFFICES);
  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.ORG_OFFICES,
  );
  const [companyConfig, openCompany, closeCompany] = useAppDiscloser(
    DISCLOSER_KEYS.ORG_COMPANY,
  );

  const organizationInfo = profileInfo?.organization;

  // Fetch company details
  const { data: companyDetails } = useGetCompanyDetailsQuery();

  // Update company mutation
  const [updateCompany, { isLoading: updatingCompany, error: updateError }] =
    useUpdateCompanyMutation();

  // API call for offices
  const {
    data: officesData,
    isLoading,
    isFetching,
  } = useGetAllOfficesQuery({
    params: {
      ...filters,
      skip: skip,
      take: limit,
      search: keyword,
    },
  });

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const res = await uploadFileToBucket(file, access_token as string);
      if (res.success) {
        // Update company logo
        await updateCompany({
          logo_url: res.url,
        }).unwrap();

        toast('success', 'Company logo uploaded successfully');
      } else {
        toast('error', getErrorMessage(res || 'Failed to upload.'));
      }
    } catch (error) {
      console.error('Upload Error:', error);
      errorToast(updateError, 'An error occurred during upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Office Name',
      dataIndex: 'name',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (val) => val ?? 'n/a',
    },
    // {
    //   title: 'Parent Office',
    //   dataIndex: 'parent_office',
    //   render: (parentOffice) => parentOffice?.name ?? 'n/a',
    // },
    // {
    //   title: 'Location',
    //   dataIndex: 'location',
    //   render: (val) => val ?? 'n/a',
    // },
    {
      title: 'Address',
      dataIndex: 'address',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Employee',
      dataIndex: 'total_employees',
      render: (val) => normalizeColumnValue(val, '0'),
    },
    {
      title: 'GEO Fence',
      dataIndex: 'geo_fence',
      align: 'center',
      render: (geoFence: boolean) => (
        <StatusTag
          value={geoFence ? 'active' : 'inactive'}
          config={GEO_STATUS_CONFIG}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={OFFICES_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Action',
      dataIndex: 'id',
      align: 'center',
      render: (id: string) => (
        <Space>
          <Link to={``}>
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
              onClick={() => handleOpen({ type: 'VIEW', data: { id } })}
            />
          </Link>

          <Button
            size="small"
            className="action-btn"
            icon={<PencilEdit02Icon size={20} />}
            onClick={() => handleOpen({ type: 'UPDATE', data: { id } })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <WithLoader isLoading={false} className="rounded-xl">
        <Flex
          justify="space-between"
          className="px-[35px] py-10 bg-theme-slate-800 rounded-xl mt-6 items-start"
        >
          <div className="flex flex-wrap justify-between items-start gap-5 w-[85%]">
            <Flex align="start" gap={24}>
              <div className="relative">
                <WithLoader
                  isLoading={isUploading || updatingCompany}
                  className="rounded-full"
                >
                  <CustomImage
                    src={
                      companyDetails?.data?.logo_url ||
                      organizationInfo?.logoUrl ||
                      IMAGES.placeholder
                    }
                    size={95}
                    className="rounded-full object-cover"
                  />
                </WithLoader>
                <button
                  onClick={handleUploadClick}
                  disabled={isUploading || updatingCompany}
                  className="absolute right-[7px] bottom-[7px] bg-theme-slate-900 rounded-full"
                >
                  <CirclePlusIcon size={18} className="text-white" />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
              <div className="space-y-3">
                <div className="pb-3 border-b-[0.5px] border-white flex justify-start items-center gap-2">
                  <div className="text-2xl text-white font-black">
                    {companyDetails?.data?.company_name ||
                      organizationInfo?.name ||
                      'Organization Name'}
                  </div>
                  <Button
                    size="small"
                    className="action-btn"
                    icon={
                      <PencilEdit02Icon size={20} className="text-[#00C6AE]" />
                    }
                    onClick={() =>
                      openCompany({
                        type: 'UPDATE',
                        data: companyDetails,
                      })
                    }
                  />
                </div>

                <div className="scroll-pt-4">
                  <div className=" text-white text-lg font-medium">
                    {companyDetails?.data?.company_name ||
                      organizationInfo?.name ||
                      'Head Office'}
                  </div>
                  <div className=" text-white text-lg font-medium">
                    {companyDetails?.data?.timezone
                      ? getLabelByValue(companyDetails?.data?.timezone)
                      : organizationInfo?.meta?.time_zone
                        ? getLabelByValue(organizationInfo?.meta?.time_zone)
                        : '--'}
                  </div>
                  <div className=" text-white text-lg font-medium">
                    {organizationInfo?.meta?.industry?.name || 'N/A'}
                  </div>
                  <div className=" text-white text-lg font-medium">
                    {companyDetails?.data?.total_employee_count || '0'}{' '}
                    Employees
                  </div>
                </div>
              </div>
            </Flex>

            <div className="space-y-4">
              <div className="flex  flex-wrap lg:flex-nowrap !justify-between  items-center gap-5">
                <div className="flex flex-col gap-4">
                  <InfoItem
                    label="Admin Name :"
                    value={
                      companyDetails?.data?.admin_name ||
                      organizationInfo?.meta?.admin_name ||
                      'N/A'
                    }
                  />
                  <InfoItem
                    label="Email :"
                    value={
                      companyDetails?.data?.admin_email ||
                      organizationInfo?.meta?.admin_email ||
                      'N/A'
                    }
                  />
                  <InfoItem
                    label="Contact Number :"
                    value={
                      companyDetails?.data?.admin_phone ||
                      organizationInfo?.meta?.admin_phone ||
                      'N/A'
                    }
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <InfoItem
                    label="Contact Person Name :"
                    value={
                      companyDetails?.data?.contact_person_name ||
                      organizationInfo?.meta?.contact_person_name ||
                      'N/A'
                    }
                  />
                  <InfoItem
                    label="Email :"
                    value={
                      companyDetails?.data?.contact_person_email ||
                      organizationInfo?.meta?.contact_person_email ||
                      'N/A'
                    }
                  />
                  <InfoItem
                    label="Contact Number :"
                    value={
                      companyDetails?.data?.contact_person_phone ||
                      organizationInfo?.meta?.contact_person_phone ||
                      'N/A'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <p className="text-base text-white">
              {organizationInfo?.isActive !== false ? 'Active' : 'Inactive'}
            </p>
            <Switch checked className="custom-switch" />
          </div>
        </Flex>
      </WithLoader>

      <div className="mt-5">
        <CustomTable
          page={page}
          limit={limit}
          hideRowSelect
          columns={columns}
          tableRootClassName="border-0 p-0"
          dataSource={officesData?.data?.data || []}
          onPaginate={handlePagination}
          customTitle="Offices"
          loading={isLoading || isFetching}
          total={officesData?.data?.meta?.total || 0}
        />
      </div>

      <ManageOffices
        id={config.data?.id}
        open={config.open}
        type={config.type}
        handleClose={handleClose}
      />

      <ManageCompany
        data={companyDetails}
        open={companyConfig.open}
        handleClose={closeCompany}
      />
    </div>
  );
};

export default Offices;

const InfoItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="w-full">
      <h6 className="text-base text-white">{label}</h6>
      <h6 className="text-xl text-white w-[250px]">{value}</h6>
    </div>
  );
};
